import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import BrandList from '@features/crm/brands/BrandList';
import FilterComponent from '@features/crm/components/FilterComponent';
import SalesFunel from '@features/crm/sales-funel';
import CRMDashboard from '@features/crm/v3/dashboard/CRMDashboard';
import TargetList from '@features/crm/v3/target/TargetList';

import { toggleCRMLeftSidePanel } from '@store/actions/crm/CRMCommonAction';
import { clearGlobalFilter, updateTextItemInGlobalFilter } from '@store/actions/FilterAction';

import { fetchTargetTeamMemberList } from '@services/crmService';

import { changeDateFormat, crmTabs } from '@utils/Util';

const CrmWrapper = () => {
	const { DASHBOARD, BRANDS, TARGET, CONFIGURATION, SALES_FUNNEL } = crmTabs;
	const [activeTab, setActiveTab] = useState(null);
	const [openModal, setOpenModal] = useState(false);
	const [isOpenFilter, setIsOpenFilter] = useState(false);
	const [members, setMembers] = useState([]);
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { search } = useLocation();

	const searchParams = useMemo(() => new URLSearchParams(search), [search]);

	const fetchAllMembers = async () => {
		let workflowMembers = await fetchTargetTeamMemberList();
		workflowMembers = workflowMembers?.map((item) => ({ value: item.name, id: item.id }));
		setMembers(workflowMembers);
	};

	useEffect(() => {
		let tabName = searchParams.get('activeTab');
		if (tabName !== null) {
			setActiveTab(tabName);
			dispatch(clearGlobalFilter());
			let date = new Date();
			let fromDate = `${date.getFullYear()}-01-01`;
			let toDate = `${date.getFullYear()}-12-31`;
			if (tabName === DASHBOARD) {
				let data = {
					name: 'dateRange',
					key: fromDate + ',' + toDate,
					value:
						changeDateFormat(fromDate, 'YYYY-MM-DD', 'DD MMM, YYYY') +
						' - ' +
						changeDateFormat(toDate, 'YYYY-MM-DD', 'DD MMM, YYYY'),
					identifier: 'dateRange',
					additionalData: 'date-range-picker-input-11',
					isHideChip: true
				};
				dispatch(updateTextItemInGlobalFilter(data));
			}
		} else {
			navigate(`/crm?activeTab=${SALES_FUNNEL}`);
		}
	}, [searchParams]);

	useEffect(() => {
		if (activeTab === DASHBOARD || activeTab === TARGET) {
			(async () => {
				try {
					await fetchAllMembers();
				} catch {
					/* Empty Block */
				}
			})();
		}
	}, [activeTab]);

	const renderTabContents = () => {
		switch (activeTab) {
			case SALES_FUNNEL:
				return (
					<SalesFunel
						onChangeTab={onTabChange}
						toggleFilter={() => setIsOpenFilter((prev) => !prev)}
						isShowFilter={isOpenFilter}
					/>
				);
			case BRANDS:
				return <BrandList openModal={openModal} triggerModal={setOpenModal} />;
			case TARGET:
				return (
					<TargetList
						openModal={openModal}
						triggerModal={setOpenModal}
						isIndividualUser={members.length === 0}
					/>
				);
			case DASHBOARD:
				return (
					<CRMDashboard
						openModal={openModal}
						triggerModal={setOpenModal}
						isIndividualUser={members.length === 0}
					/>
				);
			default:
				return <></>;
		}
	};

	const onTabChange = (tabName) => {
		navigate(`/crm?activeTab=${tabName}`);
		window.scrollTo(0, 0);
	};

	return (
		<>
			<div className="top-header bg-white">
				<div className="page-title-area">
					<h1 className="semibold-16">CRM</h1>
				</div>
				<div className="navigation-tab shadow-nav-4dp">
					<ul>
						<li
							onClick={() => onTabChange(DASHBOARD)}
							className={activeTab === DASHBOARD ? 'active' : ''}
						>
							Dashboard
						</li>
						<li
							onClick={() => {
								onTabChange(SALES_FUNNEL);
							}}
							className={activeTab === SALES_FUNNEL ? 'active' : ''}
						>
							Sales Pipeline
						</li>
						<li
							onClick={() => {
								onTabChange(TARGET);
								dispatch(toggleCRMLeftSidePanel(true));
							}}
							className={activeTab === TARGET ? 'active' : ''}
						>
							Target
						</li>
						<li
							onClick={() => onTabChange(BRANDS)}
							className={activeTab === BRANDS ? 'active' : ''}
						>
							Brands
						</li>
					</ul>
				</div>
				{![CONFIGURATION].includes(activeTab) && (
					<FilterComponent
						openModal={openModal}
						activeTab={activeTab}
						setOpenModal={setOpenModal}
						toggleFilter={() => setIsOpenFilter((prev) => !prev)}
						isIndividualUser={members.length === 0}
					/>
				)}
			</div>
			<section className={activeTab === TARGET ? 'body-content' : ''}>{renderTabContents()}</section>
		</>
	);
};

export default CrmWrapper;
